import { List } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Container, Text } from "@/index";
import { getDocsPages, type DocsFramework } from "./docs/docsCatalog";

type RouteState = {
  pageId: string;
  framework: DocsFramework;
};

const frameworkOptions: Array<{ id: DocsFramework; label: string }> = [
  { id: "react", label: "React" },
  { id: "angular", label: "Angular" }
];

function createDocsHref(pageId: string, framework: DocsFramework) {
  return `?section=${encodeURIComponent(pageId)}&framework=${framework}`;
}

function readRouteState(): RouteState {
  if (typeof window === "undefined") {
    return { pageId: "installation", framework: "react" };
  }

  const params = new window.URLSearchParams(window.location.search);
  const hashPageId = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : "";
  const pageId = params.get("section") || hashPageId || "installation";
  const framework = params.get("framework") === "angular" ? "angular" : "react";

  return { pageId, framework };
}

function writeRouteState(pageId: string, framework: DocsFramework, replace = false) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("section", pageId);
  url.searchParams.set("framework", framework);
  url.hash = "";

  window.history[replace ? "replaceState" : "pushState"](null, "", `${url.pathname}${url.search}`);
}

export function ComponentShowcasePage() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [routeState, setRouteState] = useState<RouteState>(() => readRouteState());
  const docsPages = useMemo(() => getDocsPages(routeState.framework), [routeState.framework]);
  const selectedPage = useMemo(
    () => docsPages.find((page) => page.id === routeState.pageId) ?? docsPages[0],
    [docsPages, routeState.pageId]
  );
  const pagesByCategory = useMemo(
    () => Array.from(new Set(docsPages.map((page) => page.category))).map((category) => ({ category, pages: docsPages.filter((page) => page.category === category) })),
    [docsPages]
  );

  useEffect(() => {
    const syncRoute = () => setRouteState(readRouteState());

    window.addEventListener("popstate", syncRoute);
    window.addEventListener("hashchange", syncRoute);

    return () => {
      window.removeEventListener("popstate", syncRoute);
      window.removeEventListener("hashchange", syncRoute);
    };
  }, []);

  useEffect(() => {
    const params = new window.URLSearchParams(window.location.search);
    const sectionParam = params.get("section");
    const frameworkParam = params.get("framework");

    if (sectionParam !== selectedPage.id || frameworkParam !== routeState.framework || window.location.hash) {
      writeRouteState(selectedPage.id, routeState.framework, true);
    }

    if (routeState.pageId !== selectedPage.id) {
      setRouteState({ ...routeState, pageId: selectedPage.id });
    }
  }, [routeState, selectedPage.id]);

  const selectPage = useCallback((pageId: string) => {
    if (routeState.pageId !== pageId) {
      writeRouteState(pageId, routeState.framework);
      setRouteState({ ...routeState, pageId });
    }

    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [routeState]);

  const selectFramework = useCallback((framework: DocsFramework) => {
    if (routeState.framework === framework) {
      return;
    }

    writeRouteState(routeState.pageId, framework);
    setRouteState({ ...routeState, framework });
  }, [routeState]);

  return (
    <div className="bfrs-min-h-dvh bfrs-bg-surface-app bfrs-text-text-primary">
      <header className="bfrs-sticky bfrs-top-0 bfrs-z-40 bfrs-border-b bfrs-border-border bfrs-bg-surface-card/95 bfrs-backdrop-blur">
        <Container size="full" className="bfrs-flex bfrs-h-16 bfrs-items-center bfrs-justify-between">
          <a
            href={createDocsHref("installation", routeState.framework)}
            className="bfrs-font-bold bfrs-text-text-primary"
            onClick={(event) => {
              event.preventDefault();
              selectPage("installation");
            }}
          >
            Shiprocket UI
          </a>
          <div className="bfrs-flex bfrs-items-center bfrs-gap-2">
            <div className="bfrs-inline-flex bfrs-h-9 bfrs-items-center bfrs-rounded-md bfrs-border bfrs-border-border bfrs-bg-surface-muted bfrs-p-0.5" aria-label="Documentation framework">
              {frameworkOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={routeState.framework === option.id}
                  className={`bfrs-h-8 bfrs-rounded bfrs-px-3 bfrs-text-sm bfrs-font-semibold bfrs-transition focus-visible:bfrs-outline-none focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus ${
                    routeState.framework === option.id
                      ? "bfrs-bg-surface-card bfrs-text-text-primary bfrs-shadow-sm"
                      : "bfrs-text-text-muted hover:bfrs-text-text-primary"
                  }`}
                  onClick={() => selectFramework(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button className="lg:bfrs-hidden" variant="outline" size="sm" leftIcon={<List size={16} />} onClick={() => setMobileNavOpen((open) => !open)}>
              Menu
            </Button>
          </div>
        </Container>
      </header>

      <div className="bfrs-grid lg:bfrs-grid-cols-[280px_minmax(0,1fr)]">
        <aside className={`bfrs-border-r bfrs-border-border bfrs-bg-surface-card lg:bfrs-sticky lg:bfrs-top-16 lg:bfrs-block lg:bfrs-h-[calc(100dvh-4rem)] lg:bfrs-overflow-y-auto ${mobileNavOpen ? "bfrs-block" : "bfrs-hidden"}`}>
          <nav className="bfrs-p-4" aria-label="Component documentation">
            {pagesByCategory.map(({ category, pages }) => (
              <div key={category} className="bfrs-mb-5">
                <p className="bfrs-mb-2 bfrs-px-3 bfrs-text-[11px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.14em] bfrs-text-text-disabled">{category}</p>
                <div className="bfrs-grid bfrs-gap-1">
                  {pages.map((page) => (
                    <a
                      key={page.id}
                      href={createDocsHref(page.id, routeState.framework)}
                      className={`bfrs-rounded-md bfrs-px-3 bfrs-py-2 bfrs-text-sm bfrs-font-medium bfrs-transition focus-visible:bfrs-outline-none focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus ${
                        selectedPage.id === page.id
                          ? "bfrs-bg-surface-muted bfrs-text-text-primary bfrs-shadow-sm"
                          : "bfrs-text-text-secondary hover:bfrs-bg-surface-muted hover:bfrs-text-text-primary"
                      }`}
                      onClick={(event) => {
                        event.preventDefault();
                        selectPage(page.id);
                      }}
                    >
                      {page.title}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <main className="bfrs-min-w-0">
          <Container size="lg" className="bfrs-py-10">
            <section id={selectedPage.id} key={`${routeState.framework}-${selectedPage.id}`} className="bfrs-scroll-mt-24">
              <div className="bfrs-mb-5 bfrs-max-w-4xl">
                <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-3">
                  <Text variant="label" tone="accent">{selectedPage.category}</Text>
                  <span className="bfrs-rounded bfrs-border bfrs-border-border bfrs-bg-surface-muted bfrs-px-2 bfrs-py-1 bfrs-text-xs bfrs-font-semibold bfrs-text-text-muted">
                    {routeState.framework === "angular" ? "Angular" : "React"}
                  </span>
                </div>
                <Text variant="h1" className="bfrs-mt-2">{selectedPage.title}</Text>
                {selectedPage.description && <Text variant="body-sm" tone="muted" className="bfrs-mt-2 bfrs-max-w-3xl">{selectedPage.description}</Text>}
              </div>
              {selectedPage.content}
            </section>
          </Container>
        </main>
      </div>
    </div>
  );
}
